/* eslint-disable no-unused-vars */

import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/solid";
import { useAuth } from "../../context/AuthContext";
import { useBusinessProfile } from "../../context/BusinessProfileContext";
import pebbleLogo from "../../assets/images/pebble-logo.png";
import DOMAIN_CONFIG from "../../config/domains";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

const StripeConnectCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [needsMoreInfo, setNeedsMoreInfo] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const {
    user,
    refreshUserData,
    hasActiveSubscription,
    userType,
    initialAuthCheckDone,
  } = useAuth();
  const { updateBusinessProfile } = useBusinessProfile();
  const callbackProcessed = useRef(false);

  // Determine if we're in development mode
  const isDev =
    process.env.NODE_ENV === "development" ||
    window.location.hostname === "localhost" ||
    window.location.hostname.includes("dev");

  useEffect(() => {
    const handleStripeConnectCallback = async () => {
      // Wait for auth to be ready
      if (!initialAuthCheckDone) {
        console.log("Waiting for auth check to complete...");
        return;
      }

      // Prevent double processing
      if (callbackProcessed.current) {
        return;
      }

      const urlParams = new URLSearchParams(location.search);
      const successParam = urlParams.get("success");
      const refreshParam = urlParams.get("refresh");
      const accountId = urlParams.get("account_id");
      const errorParam = urlParams.get("error");
      const errorDescription = urlParams.get("error_description");

      console.log("Stripe Connect callback received:", {
        success: successParam,
        refresh: refreshParam,
        accountId,
        hasError: !!errorParam,
        isDev,
        hasUser: !!user,
        userId: user?.uid,
      });

      if (errorParam) {
        setError(
          errorDescription ||
            "An error occurred during the Stripe connection process.",
        );
        setIsLoading(false);
        return;
      }

      // Handle explicit cancellation (success=false)
      if (successParam === "false") {
        setCancelled(true);
        setIsLoading(false);
        return;
      }

      // Check multiple sources for the account ID with fallbacks
      let storedAccountId = accountId;

      if (!storedAccountId) {
        const storedUserId =
          sessionStorage.getItem("stripeConnectUserId") ||
          localStorage.getItem("stripeConnectUserId");

        if (user && storedUserId === user.uid) {
          storedAccountId =
            sessionStorage.getItem("stripeConnectAccountId") ||
            localStorage.getItem("stripeConnectAccountId") ||
            sessionStorage.getItem("stripeAccountId");
        } else if (user && storedUserId && storedUserId !== user.uid) {
          console.log("Clearing stale Stripe data from different user");
          sessionStorage.removeItem("stripeConnectAccountId");
          sessionStorage.removeItem("stripeConnectUserId");
          localStorage.removeItem("stripeConnectAccountId");
          localStorage.removeItem("stripeConnectUserId");
        }
      }

      if (!storedAccountId) {
        setError(
          "No account information found. Please start the connection process again from settings.",
        );
        setIsLoading(false);
        return;
      }

      // Store it in sessionStorage for the rest of this flow if we got it from URL
      if (accountId && !sessionStorage.getItem("stripeConnectAccountId")) {
        sessionStorage.setItem("stripeConnectAccountId", accountId);
        if (user) {
          sessionStorage.setItem("stripeConnectUserId", user.uid);
        }
      }

      // Handle refresh - need to continue onboarding
      if (refreshParam === "true") {
        try {
          const functions = getFunctions();
          const createAccountLink = httpsCallable(
            functions,
            "createStripeConnectAccountLink",
          );
          const result = await createAccountLink({
            accountId: storedAccountId,
          });
          const data = result.data;

          if (data.success && data.onboardingUrl) {
            window.location.href = data.onboardingUrl;
          } else {
            setError(
              "Failed to continue onboarding. Please try again from settings.",
            );
            setIsLoading(false);
          }
        } catch (err) {
          console.error("Error refreshing account link:", err);
          setError("An error occurred. Please try again from settings.");
          setIsLoading(false);
        }
        return;
      }

      // Handle success - save status directly to Firestore
      if (successParam === "true" && storedAccountId && user) {
        callbackProcessed.current = true;

        try {
          console.log("Saving Stripe Connect status directly to Firestore...");
          console.log("User ID:", user.uid);
          console.log("Account ID:", storedAccountId);

          // Force token refresh to ensure Firestore has valid credentials after redirect
          await user.getIdToken(true);
          console.log("Token refreshed successfully");

          const now = serverTimestamp();

          // Save to stripeData collection
          console.log("Saving to stripeData collection...");
          await setDoc(
            doc(db, "stripeData", user.uid),
            {
              accountId: storedAccountId,
              stripe_user_id: storedAccountId,
              success: true,
              chargesEnabled: true,
              payoutsEnabled: true,
              detailsSubmitted: true,
              accountType: "express",
              environment: isDev ? "development" : "production",
              createdAt: now,
              updatedAt: now,
            },
            { merge: true },
          );
          console.log("stripeData saved successfully");

          // Update users collection
          console.log("Saving to users collection...");
          await setDoc(
            doc(db, "users", user.uid),
            {
              stripeConnected: true,
              stripeUserId: storedAccountId,
              stripeAccountStatus: "active",
              updatedAt: now,
            },
            { merge: true },
          );
          console.log("users saved successfully");

          // Update EO_BusinessProfiles collection
          console.log("Saving to EO_BusinessProfiles collection...");
          await setDoc(
            doc(db, "EO_BusinessProfiles", user.uid),
            {
              stripeAccountConnected: true,
              stripeAccountId: storedAccountId,
              updatedAt: now,
            },
            { merge: true },
          );
          console.log("EO_BusinessProfiles saved successfully");

          console.log("Stripe Connect status saved to Firestore successfully");

          // Update local business profile
          await updateBusinessProfile({
            stripeAccountConnected: true,
            stripeAccountId: storedAccountId,
          });

          // Clean up all storage locations
          sessionStorage.removeItem("stripeConnectAccountId");
          sessionStorage.removeItem("stripeConnectUserId");
          sessionStorage.removeItem("stripeAccountId");
          localStorage.removeItem("stripeConnectAccountId");
          localStorage.removeItem("stripeConnectUserId");

          if (refreshUserData) {
            await refreshUserData();
          }

          setSuccess(true);
          setIsLoading(false);

          // Check if we should return to a specific page (e.g., event creation flow)
          const returnTo = sessionStorage.getItem('stripeConnectReturnTo');
          sessionStorage.removeItem('stripeConnectReturnTo');

          // Start fade out after 1.5s, then redirect after fade completes
          setTimeout(() => {
            setFadeOut(true);
          }, 1500);

          setTimeout(() => {
            if (returnTo) {
              navigate(returnTo, { replace: true });
            } else if (userType === "EO" && !hasActiveSubscription) {
              DOMAIN_CONFIG.redirectToApp("/eo-paywall");
            } else if (userType === "EO") {
              DOMAIN_CONFIG.redirectToApp("/eo/dashboard");
            } else {
              DOMAIN_CONFIG.redirectToApp("/mps/dashboard");
            }
          }, 2000);
        } catch (err) {
          console.error("Error saving Stripe Connect status:", err);
          console.error("Error code:", err.code);
          console.error("Error message:", err.message);
          setError(
            `An error occurred while saving your account: ${err.message ||
              err}`,
          );
          setIsLoading(false);
        }
        return;
      }

      // If we have success param and account but no user, show auth error
      if (successParam === "true" && storedAccountId && !user) {
        setError(
          "You must be logged in to complete Stripe setup. Please log in and try again.",
        );
        setIsLoading(false);
        return;
      }

      // Debug: log why we reached the invalid callback state
      console.log("Invalid callback - conditions not met:", {
        successParam,
        storedAccountId,
        hasUser: !!user,
        userId: user?.uid,
      });

      setError(
        `Invalid callback. Missing: ${!successParam ? "success param" : ""} ${
          !storedAccountId ? "account ID" : ""
        } ${!user ? "user auth" : ""}`.trim() ||
          "Please start the connection process again from settings.",
      );
      setIsLoading(false);
    };

    handleStripeConnectCallback();
  }, [
    location,
    navigate,
    updateBusinessProfile,
    refreshUserData,
    user,
    hasActiveSubscription,
    userType,
    isDev,
    initialAuthCheckDone,
  ]);

  const handleContinueOnboarding = async () => {
    const urlParams = new URLSearchParams(location.search);
    const accountId =
      urlParams.get("account_id") ||
      sessionStorage.getItem("stripeConnectAccountId");

    if (!accountId) {
      navigate("/eo/settings");
      return;
    }

    setIsLoading(true);

    try {
      const functions = getFunctions();
      const createAccountLink = httpsCallable(
        functions,
        "createStripeConnectAccountLink",
      );
      const result = await createAccountLink({ accountId });
      const data = result.data;

      if (data.success && data.onboardingUrl) {
        window.location.href = data.onboardingUrl;
      } else {
        setError(
          "Failed to continue onboarding. Please try again from settings.",
        );
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Error creating account link:", err);
      setError("An error occurred. Please try again from settings.");
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    navigate("/eo/settings");
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-grey-100 p-4">
      <div
        className={`bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center transition-all duration-300 ${
          success ? "animate-fade-in" : ""
        }`}
      >
        {isLoading ? (
          <div className="flex flex-col items-center gap-4">
            <p className="text-lg text-grey-700">
              Connecting your Stripe account...
            </p>
            <p className="text-sm text-grey-500">
              Please wait while we complete the setup.
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-4">
            <XCircleIcon className="h-12 w-12 text-red-500" />
            <p className="text-lg text-grey-700 font-medium">
              Connection Failed
            </p>
            <p className="text-sm text-grey-600">{error}</p>
            <div className="flex flex-col gap-2 mt-4 w-full">
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-grey-700 text-white rounded-lg hover:bg-grey-800 transition-colors"
              >
                Go to Settings
              </button>
            </div>
          </div>
        ) : success ? (
          <div
            className={`flex flex-col items-center gap-4 transition-opacity duration-500 ${
              fadeOut ? "opacity-0" : "opacity-100"
            }`}
          >
            <img src={pebbleLogo} alt="Pebble" className="h-10 mb-2" />
            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center">
              <CheckCircleIcon className="h-10 w-10 text-primary-500" />
            </div>
            <div className="text-center">
              <p className="text-xl font-semibold text-grey-900">
                Payment Connected!
              </p>
              <p className="text-sm text-grey-500 mt-1">
                Your Stripe account is ready to receive payments
              </p>
            </div>
          </div>
        ) : needsMoreInfo ? (
          <div className="flex flex-col items-center gap-4">
            <svg
              className="h-12 w-12 text-yellow-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-lg text-grey-700 font-medium">
              Additional Information Required
            </p>
            <p className="text-sm text-grey-600">
              Your Stripe account needs more information before you can accept
              payments.
            </p>
            <div className="flex flex-col gap-2 mt-4 w-full">
              <button
                onClick={handleContinueOnboarding}
                className="px-4 py-2 bg-[#635BFF] text-white rounded-lg hover:bg-[#5851EA] transition-colors"
              >
                Continue Setup
              </button>
              <button
                onClick={handleRetry}
                className="px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition-colors"
              >
                Go to Settings
              </button>
            </div>
          </div>
        ) : cancelled ? (
          <div className="flex flex-col items-center gap-4">
            <XCircleIcon className="h-12 w-12 text-grey-400" />
            <p className="text-lg text-grey-700 font-medium">
              Connection Cancelled
            </p>
            <p className="text-sm text-grey-600">
              You cancelled the Stripe connection process. You can try again
              whenever you're ready.
            </p>
            <div className="flex flex-col gap-2 mt-4 w-full">
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-grey-700 text-white rounded-lg hover:bg-grey-800 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StripeConnectCallback;
